import type { UserRole } from '../types/crm';

const USER_ROLES: UserRole[] = ['user', 'supervisor', 'management', 'admin', 'it', 'finance'];

/** Normalize DB / form values for role comparisons. Supervisor is retired → user. */
export function normalizeUserRole(role: string | null | undefined): UserRole {
  const value = (role ?? 'user').trim().toLowerCase();
  if (value === 'supervisor') return 'user';
  return USER_ROLES.includes(value as UserRole) ? (value as UserRole) : 'user';
}

export function canViewSettings(role: UserRole): boolean {
  return role === 'it' || role === 'admin' || role === 'management';
}

export function canManageAllowedEmails(role: UserRole): boolean {
  return canViewSettings(role);
}

export function canUseViewAs(role: UserRole, elevatedDepartment?: string | null): boolean {
  if (elevatedDepartment === 'it') return true;
  if (elevatedDepartment === 'management') return false;
  return role === 'it';
}

export function canUseUserFilter(role: UserRole): boolean {
  return role === 'it' || role === 'admin' || role === 'management';
}

/** @deprecated Use fleetPermissions.canShowFleetNav / canAccessFleet instead. */
export function canAccessFleet(role: UserRole): boolean {
  return normalizeUserRole(role) !== 'user';
}

/** Who may delete an allowlist row with the given role. */
export function canDeleteAllowedEmail(actorRole: UserRole, targetRole: UserRole): boolean {
  const actor = normalizeUserRole(actorRole);
  const target = normalizeUserRole(targetRole);
  if (actor === 'it') return true;
  if (actor === 'management') return target !== 'it';
  if (actor === 'admin') return target !== 'it';
  return false;
}

/** Who may edit display name / role on an allowlist row. */
export function canEditAllowedEmail(actorRole: UserRole, targetRole: UserRole): boolean {
  return canDeleteAllowedEmail(actorRole, targetRole);
}

/** Roles the actor may assign when adding or editing allowlist users. */
export function assignableAllowlistRoles(actorRole: UserRole): UserRole[] {
  const actor = normalizeUserRole(actorRole);
  // Supervisor retired — only user / admin as portal business roles.
  if (actor === 'admin') {
    return ['user', 'admin'];
  }
  // IT / Management (department-elevated) assign business roles only — privilege is via department.
  if (actor === 'it' || actor === 'management') {
    return ['user', 'admin'];
  }
  return [];
}

const ALLOWLIST_ROLE_LABELS: Record<UserRole, string> = {
  user: 'User',
  supervisor: 'Supervisor',
  management: 'Management',
  admin: 'Admin',
  it: 'IT',
  finance: 'Finance',
};

/** Dropdown options (value + label) for allowlist add/edit, in display order. */
export function assignableAllowlistRoleOptions(actorRole: UserRole): { value: UserRole; label: string }[] {
  const allowed = new Set(assignableAllowlistRoles(actorRole));
  return (['user', 'admin'] as UserRole[])
    .filter((role) => allowed.has(role))
    .map((value) => ({ value, label: ALLOWLIST_ROLE_LABELS[value] }));
}

export function allowlistRoleLabel(role: UserRole): string {
  return ALLOWLIST_ROLE_LABELS[normalizeUserRole(role)];
}

export type FormsPortalRole = 'user' | 'supervisor' | 'admin' | 'management' | 'it';

const FORMS_PORTAL_ROLE_LABELS: Record<FormsPortalRole, string> = {
  user: 'User',
  supervisor: 'Supervisor',
  admin: 'Admin',
  management: 'Management',
  it: 'IT',
};

/** Roles assignable on Application Form portal (Admin Hub Forms column). */
export function assignableFormsPortalRoleOptions(
  actorRole: UserRole,
): { value: FormsPortalRole; label: string }[] {
  const actor = normalizeUserRole(actorRole);
  // Forms Admin temporarily same access as User; both remain assignable for future use.
  if (actor === 'it' || actor === 'management') {
    return (['user', 'admin'] as FormsPortalRole[]).map((value) => ({
      value,
      label: FORMS_PORTAL_ROLE_LABELS[value],
    }));
  }
  return [];
}

export function canAssignFormsPortalRole(
  actorRole: UserRole,
  targetCurrentRole: FormsPortalRole | 'none',
  nextRole: FormsPortalRole | 'none',
): boolean {
  const actor = normalizeUserRole(actorRole);
  if (nextRole === 'none') {
    if (targetCurrentRole === 'none') return false;
    return actor === 'it' || actor === 'management';
  }
  if (actor === 'it' || actor === 'management') {
    return assignableFormsPortalRoleOptions(actor).some((option) => option.value === nextRole);
  }
  return false;
}

export function canEditFormsPortalRole(actorRole: UserRole, _targetRole: FormsPortalRole): boolean {
  const actor = normalizeUserRole(actorRole);
  return actor === 'it' || actor === 'management';
}

export function canAddFormsPortalWithRole(actorRole: UserRole, newRole: FormsPortalRole): boolean {
  return assignableFormsPortalRoleOptions(actorRole).some((option) => option.value === newRole);
}

/** Whether actor may add a new allowlist user with newRole. */
export function canAddAllowlistWithRole(actorRole: UserRole, newRole: UserRole): boolean {
  return assignableAllowlistRoles(actorRole).includes(normalizeUserRole(newRole));
}

/** Whether actor may set target user to newRole (add or edit). */
export function canAssignAllowlistRole(
  actorRole: UserRole,
  targetCurrentRole: UserRole,
  newRole: UserRole,
): boolean {
  const actor = normalizeUserRole(actorRole);
  const current = normalizeUserRole(targetCurrentRole);
  const next = normalizeUserRole(newRole);

  if (!canEditAllowedEmail(actor, current)) return false;
  return assignableAllowlistRoles(actor).includes(next);
}

export function assertAllowlistUpdateAllowed(
  actorRole: UserRole,
  targetCurrentRole: UserRole,
  patch: { display_name?: string | null; role?: UserRole },
): void {
  const actor = normalizeUserRole(actorRole);
  const current = normalizeUserRole(targetCurrentRole);

  if (!canEditAllowedEmail(actor, current)) {
    if (actor === 'admin' && current === 'it') {
      throw new Error('Admin cannot edit IT users.');
    }
    if (actor === 'management') {
      throw new Error('Management cannot edit IT users.');
    }
    throw new Error('You cannot edit this allowlist entry.');
  }

  if (patch.role !== undefined && !canAssignAllowlistRole(actor, current, patch.role)) {
    if (actor === 'admin' && normalizeUserRole(patch.role) === 'it') {
      throw new Error('Admin cannot assign the IT role.');
    }
    if (actor === 'admin' && normalizeUserRole(patch.role) === 'management') {
      throw new Error('Admin cannot assign the Management role.');
    }
    if (actor === 'management' && normalizeUserRole(patch.role) === 'it') {
      throw new Error('Management cannot assign the IT role.');
    }
    throw new Error('You cannot assign this role.');
  }
}

export function canManageSharePointSettings(role: UserRole): boolean {
  return role === 'it';
}

export function deleteAllowedEmailHint(actorRole: UserRole): string {
  const actor = normalizeUserRole(actorRole);
  if (actor === 'it') return 'IT can edit or remove any role.';
  if (actor === 'management') {
    return 'Management can add, edit, or remove User and Admin portal roles — not IT department accounts.';
  }
  if (actor === 'admin') return 'Admin cannot edit or remove IT users, or assign Management.';
  return 'You cannot manage allowlist entries.';
}
